"use strict";

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-toastify";

import { getCharacterLabel } from "./CharacterSelector.js";
import { characterWriteErrorMessage } from "./renameCharacter.js";
import { APPEND_CHARACTER, SOFT_DELETE_CHARACTER } from "../../ApiEndpoints.js";
import classes from "../../Assets/Styles/CharacterVoicePanel.module.css";

// Characters are soft-deleted (hidden from the lists, voices kept), so a
// delete can be undone by restoring the same character.
const UndoToast = ({ name, onUndo, closeToast }) => {
  const [restoring, setRestoring] = useState(false);

  return (
    <span className={classes["UndoToast"]}>
      <span>{name} deleted</span>
      <button
        type="button"
        className={classes["UndoToastButton"]}
        disabled={restoring}
        onClick={async () => {
          setRestoring(true);
          await onUndo();
          closeToast?.();
        }}
      >
        {restoring ? "Restoring…" : "Undo"}
      </button>
    </span>
  );
};

/**
 * Confirms deleting `character` (null while closed). Calls onDeleted after the
 * delete and again after an Undo, so the caller can reload its characters.
 */
const CharacterDeleteDialog = ({ character, projectId, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const name = character ? getCharacterLabel(character) : "";

  const handleClose = () => {
    if (deleting) return;
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    const deleted = character;

    try {
      await axios.post(SOFT_DELETE_CHARACTER, {
        projectId,
        speaker: deleted.speaker,
      });
      onClose();
      await onDeleted?.(deleted.speaker);

      toast(
        ({ closeToast }) => (
          <UndoToast
            name={name}
            closeToast={closeToast}
            onUndo={async () => {
              try {
                await axios.post(APPEND_CHARACTER, {
                  projectId,
                  character: { ...deleted, isUsed: true },
                });
                await onDeleted?.(deleted.speaker);
              } catch (err) {
                console.error("Error restoring character:", err);
                toast.error(characterWriteErrorMessage(err, `Couldn't restore ${name}`));
              }
            }}
          />
        ),
        { autoClose: 8000, closeOnClick: false }
      );
    } catch (err) {
      console.error("Error deleting character:", err);
      setError(`Couldn't delete ${name}. Try again.`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={Boolean(character)}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: classes["DeleteDialogPaper"] }}
    >
      {character && (
        <>
          <DialogTitle className={classes["DeleteDialogTitle"]}>
            Delete {name}?
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <p className={classes["DeleteDialogText"]}>
                {name} is removed from this project's characters
                {character.voices?.length
                  ? `, with their ${character.voices.length} ${
                      character.voices.length === 1 ? "voice" : "voices"
                    }`
                  : ""}
                .
              </p>
              <Alert severity="warning">
                Lines assigned to {name} keep that character until you pick
                another one for them.
              </Alert>
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions className={classes["DeleteDialogActions"]}>
            <Button onClick={handleClose} disabled={deleting} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disableElevation
              onClick={handleDelete}
              disabled={deleting}
              startIcon={
                deleting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
            >
              Delete
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CharacterDeleteDialog;
